import React, { useState, useMemo, useEffect, useRef } from "react";
import debounce from "lodash/debounce";
import { useDataGridLocaleText } from "../i18n/useDataGridLocale";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  CircularProgress,
  Skeleton,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { GetApp, FilterList } from "@mui/icons-material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded"; // [NEW]
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";

const GenericTable = ({
  rows = [],
  columns = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading,
  onExportPDFTitle,
  exportFileName = "dados_exportados",
  paginationMode = "server", // "server" | "client"
  /** Quando "server", o pai deve buscar dados já ordenados (ex.: GET /products?sort_by=…). */
  sortingMode,
  sortModel,
  onSortModelChange,

  // novos/ajustados
  rightActions,
  onColumnWidthChange,
  advancedFilters,

  // [NEW] exclusão em massa
  onDeleteSelected, // (ids) => Promise|void
  deleteSelectedLabel,
  deleteConfirmTitle,
  deleteConfirmMessage,

  // [NEW] expor seleção p/ parent (opcional)
  onSelectionChange,
  onRowClick, // (row) => void 
  isRowSelectable, // (params) => boolean (DataGrid)
  /** Ações adicionais quando há seleção (ex.: unificar). */
  bulkActions, // ReactNode | ({ selectedRows, selectedCount }) => ReactNode

  /** Quando false, oculta checkboxes e export em massa (ex.: listagens sem exclusão em lote). */
  checkboxSelection = true,
  /** Busca controlada pelo parent (filtra a cada tecla). Se definido, use com `onSearchChange`. */
  searchValue,
  onSearchChange,
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchInput, setSearchInput] = useState("");
  const controlledSearch = searchValue !== undefined && typeof onSearchChange === "function";

  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  /** Server-side tables: debounce estável (callback via ref para não resetar o timer a cada render). */
  const debouncedOnSearch = useMemo(() => {
    if (controlledSearch) return null;
    return debounce((value) => {
      const normalized = value == null ? "" : String(value);
      onSearchRef.current?.(normalized);
    }, 400);
  }, [controlledSearch]);

  useEffect(() => {
    return () => debouncedOnSearch?.cancel();
  }, [debouncedOnSearch]);
  const dataGridLocaleText = useDataGridLocaleText();
  const { t } = useTranslation("common");
  const tSearch = t("genericTable.search");
  const tAdvancedFilters = t("genericTable.advancedFilters");
  const tFilters = t("genericTable.filters");
  const tExportPdf = t("genericTable.exportPdf");
  const tExportCsv = t("genericTable.exportCsv");
  const tSelectedCount = (count) => t("genericTable.selectedCount", { count });
  const tDeleteSelected = deleteSelectedLabel || t("genericTable.deleteSelected");
  const tDeleteConfirmTitle = deleteConfirmTitle || t("genericTable.deleteSelectedTitle");
  const tDeleteConfirmMessage =
    deleteConfirmMessage ||
    ((count) => t("genericTable.deleteSelectedConfirm", { count }));
  const tDeleting = t("genericTable.deleting");
  const tCancel = t("cancel");
  const tDelete = t("delete");
  const effectiveExportTitle = onExportPDFTitle || t("genericTable.exportDataTitle");
  const bulkActionsNode =
    typeof bulkActions === "function"
      ? bulkActions({ selectedRows, selectedCount: selectedRows.length })
      : bulkActions;

  // [NEW] estado de confirmação/execução da exclusão em massa
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text(effectiveExportTitle, 20, 10);
    const selectedData = rows.filter((row) => selectedRows.includes(row.id));
    const headers = columns.map((col) => col.headerName || col.field);
    const data = selectedData.map((row) => columns.map((col) => row[col.field]));
    doc.autoTable({ head: [headers], body: data });
    doc.save(`${exportFileName}.pdf`);
  };

  const selectedDataForCSV = rows.filter((row) => selectedRows.includes(row.id));
  const csvData = selectedDataForCSV.map((row) => {
    const csvRow = {};
    columns.forEach((col) => {
      csvRow[col.headerName || col.field] = row[col.field];
    });
    return csvRow;
  });

  const renderFilterField = (f) => {
    const common = {
      fullWidth: true,
      size: "small",
    };

    switch (f.type) {
      case "select":
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{f.label}</InputLabel>
            <Select
              label={f.label}
              value={f.value ?? ""}
              onChange={(e) => f.onChange?.(e.target.value)}
            >
              {(f.options || []).map((opt) => (
                <MenuItem key={String(opt.value)} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "number":
        return (
          <TextField
            {...common}
            label={f.label}
            type="number"
            value={f.value ?? ""}
            onChange={(e) => f.onChange?.(e.target.value)}
            inputProps={f.inputProps}
          />
        );
      default:
        return (
          <TextField
            {...common}
            label={f.label}
            value={f.value ?? ""}
            onChange={(e) => f.onChange?.(e.target.value)}
          />
        );
    }
  };

  const handleOpenBulkDelete = () => {
    if (selectedRows.length === 0 || !onDeleteSelected) return;
    setConfirmDeleteOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (!onDeleteSelected || selectedRows.length === 0) {
      setConfirmDeleteOpen(false);
      return;
    }
    try {
      setBulkDeleting(true);
      // deixa a política para o parent (ex: Promise.allSettled / endpoint em lote)
      await Promise.resolve(onDeleteSelected(selectedRows));
      // sucesso — limpa seleção
      setSelectedRows([]);
      onSelectionChange?.([]);
    } finally {
      setBulkDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  // evita disparar edição quando o alvo é um botão/checkbox/link etc.
  const isInteractiveTarget = (event) => {
    const el = event.target;
    return !!el.closest?.(
      'button, a, input, textarea, select, [role="button"], .MuiCheckbox-root, .MuiSwitch-root, .MuiIconButton-root'
    );
  };

  return (
    <Paper>
      <Box px={2} py={2}>
        {/* Topo: busca + filtros */}
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          alignItems={isMobile ? "stretch" : "center"}
          gap={1}
          mb={1.5}
        >
          <TextField
            fullWidth
            label={tSearch}
            variant="outlined"
            value={controlledSearch ? searchValue : searchInput}
            onChange={(e) => {
              const v = e.target.value == null ? "" : String(e.target.value);
              if (controlledSearch) {
                onSearchChange(v);
              } else {
                setSearchInput(v);
                debouncedOnSearch?.(v);
              }
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              if (controlledSearch) return;
              debouncedOnSearch?.cancel();
              const v = e.currentTarget?.value == null ? "" : String(e.currentTarget.value);
              onSearch?.(v);
            }}
          />
          {!isMobile && advancedFilters && (
            <Tooltip title={tAdvancedFilters}>
              <IconButton onClick={() => setFilterModalOpen(true)}>
                <FilterList />
              </IconButton>
            </Tooltip>
          )}
          {isMobile && advancedFilters && (
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setFilterModalOpen(true)}
            >
              {tFilters}
            </Button>
          )}
        </Box>

        {/* Slot para ações extras */}
        {rightActions && (
          <Box mb={1.5} display="flex" justifyContent="flex-end">
            {rightActions}
          </Box>
        )}

        {/* Barra contextual de ações em massa (padrão: PDF, CSV, Excluir) */}
        {checkboxSelection && selectedRows.length > 0 && (
          <Box
            display="flex"
            gap={2}
            mb={2}
            flexWrap="wrap"
            alignItems="center"
            sx={{ py: 1 }}
          >
            <Typography variant="subtitle1" color="text.secondary">
              {tSelectedCount(selectedRows.length)}
            </Typography>

            {bulkActionsNode}

            <Button
              variant="contained"
              color="primary"
              onClick={exportPDF}
              startIcon={<GetApp />}
              sx={{ textTransform: "uppercase" }}
            >
              {tExportPdf}
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<GetApp />}
              sx={{ textTransform: "uppercase" }}
            >
              <CSVLink
                data={csvData}
                filename={`${exportFileName}.csv`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                {tExportCsv}
              </CSVLink>
            </Button>

            {onDeleteSelected && (
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteOutlineRoundedIcon />}
                onClick={handleOpenBulkDelete}
                sx={{ textTransform: "uppercase" }}
              >
                {tDeleteSelected} ({selectedRows.length})
              </Button>
            )}
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {(columns || []).slice(0, 5).map((col, i) => (
                    <TableCell key={col.field || i}>
                      <Skeleton variant="text" width={col.width ? Math.min(col.width, 120) : 80} height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
                  <TableRow key={row}>
                    {(columns || []).slice(0, 5).map((col, i) => (
                      <TableCell key={col.field || i}>
                        <Skeleton variant="text" width="80%" height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            localeText={dataGridLocaleText}
            pagination
            paginationMode={paginationMode}
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick
            isRowSelectable={isRowSelectable}
            {...(checkboxSelection
              ? {
                  rowSelectionModel: selectedRows,
                  onRowSelectionModelChange: (newSelection) => {
                    setSelectedRows(newSelection);
                    onSelectionChange?.(newSelection);
                  },
                }
              : {})}
            paginationModel={{ page, pageSize }}
            rowCount={paginationMode === "server" ? rowCount : undefined}
            onPaginationModelChange={(model) => {
              onPageChange?.(model.page);
              onPageSizeChange?.(model.pageSize);
            }}
            {...(sortingMode ? { sortingMode } : {})}
            {...(typeof onSortModelChange === "function"
              ? {
                  sortModel: sortModel ?? [],
                  onSortModelChange,
                }
              : {})}
            // Use uma das duas conforme versão do DataGrid:
            onColumnWidthChange={onColumnWidthChange} // v6 (commit)
            onColumnResize={onColumnWidthChange}      // v5 (durante o resize)
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            onCellClick={(params, event) => {
              if (!onRowClick) return;
              // ignora: cliques em botões/links/inputs/checkbox...
              if (isInteractiveTarget(event)) return;
              // ignora: coluna de ações e coluna de checkbox interno
              if (params.field === "actions" || params.field === "__check__") return;
              // dispara edição com a row inteira
              onRowClick(params.row);
            }}
            // opcional: cursor "pointer" para indicar que dá pra clicar
            sx={{
              "& .MuiDataGrid-row:hover": { cursor: onRowClick ? "pointer" : "default" },
              "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" }
            }}
          />
        )}
      </Box>

      {/* Filtros Avançados */}
      {advancedFilters && (
        <Dialog
          open={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{tAdvancedFilters}</DialogTitle>
          <DialogContent dividers>
            {Array.isArray(advancedFilters.fields) && advancedFilters.fields.length > 0 ? (
              <Grid container spacing={2}>
                {advancedFilters.fields.map((f) => (
                  <Grid key={f.key} item xs={12} sm={6}>
                    {renderFilterField(f)}
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary">
                Nenhum filtro configurado.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <Box>
              <Button
                onClick={() => {
                  advancedFilters.onClear?.();
                  setFilterModalOpen(false);
                }}
              >
                Limpar
              </Button>
            </Box>
            <Box>
              <Button onClick={() => setFilterModalOpen(false)}>Fechar</Button>
              <Button
                variant="contained"
                onClick={() => {
                  advancedFilters.onApply?.();
                  setFilterModalOpen(false);
                }}
                sx={{ ml: 1 }}
              >
                Aplicar
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}

      {/* [NEW] Confirmação de exclusão em massa */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={bulkDeleting ? undefined : () => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>{tDeleteConfirmTitle}</DialogTitle>
        <DialogContent>
          <Typography>
            {bulkDeleting
              ? tDeleting
              : tDeleteConfirmMessage(selectedRows.length)}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!bulkDeleting && (
            <>
              <Button onClick={() => setConfirmDeleteOpen(false)}>{tCancel}</Button>
              <Button color="error" onClick={handleConfirmBulkDelete}>
                {tDelete}
              </Button>
            </>
          )}
          {bulkDeleting && <CircularProgress size={20} sx={{ m: 2 }} />}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GenericTable;
